import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QuestionCard from "../../components/placement_test/soal/QuestionCard";
import ProgressBar from "../../components/placement_test/soal/ProgressBar";
import { getQuestions, submitAnswer } from "../../hooks/useApi";

const PLACEMENT_TOTAL_QUESTIONS_KEY = "placement_total_questions";

const TestPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showOverview, setShowOverview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [modal, setModal] = useState({ open: false, type: "", message: "" });

  useEffect(() => {
    const initTest = async () => {
      const token = localStorage.getItem("token");
      // console.log("Cek token awal:", token || "Kosong"); // Debug
      if (!token) {
        setError("Sesi tidak valid. Silakan isi form identitas kembali.");
        setModal({
          open: true,
          type: "error",
          message: "Sesi tidak valid. Silakan isi form identitas kembali.",
        });
        setTimeout(() => navigate("/"), 2000);
        return;
      }

      try {
        // console.log("Init test dimulai..."); // Debug
        const data = await getQuestions();
        // console.log("getQuestions sukses, questions count:", data.length); // Debug
        localStorage.setItem(PLACEMENT_TOTAL_QUESTIONS_KEY, String(data.length));
        setQuestions(data);
        setLoading(false);
      } catch (err) {
        console.error("Error init test:", err.response?.status, err.response?.data || err.message);
        const errorMsg =
          err.response?.status === 401
            ? "Token tidak valid. Silakan login ulang."
            : err.response?.status === 404
            ? "Endpoint tidak ditemukan. Cek backend."
            : "Gagal memuat pertanyaan. Silakan coba lagi.";

        setError(errorMsg);
        setModal({
          open: true,
          type: "error",
          message: errorMsg,
        });
        setLoading(false);
      }
    };
    initTest();
  }, [navigate]);

  // Pilih jawaban (simpan di state sementara)
  const handleSelect = (optionId) => {
    const questionId = questions[currentIndex].id;
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  // Pindah ke soal berikutnya
  const handleNext = async () => {
    const questionId = questions[currentIndex].id;
    const selectedOptionId = answers[questionId];

    if (selectedOptionId) {
      try {
        await submitAnswer({
          question_id: questionId,
          option_id: selectedOptionId,
        });
      } catch (err) {
        console.error("Gagal submit jawaban:", err.response?.data || err.message);
        setModal({
          open: true,
          type: "error",
          message: "Jawaban gagal disimpan, coba lagi!",
        });
        return;
      }
    }

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      handleFinish();
    }
  };

  // Pindah ke soal sebelumnya
  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  // Toggle overview (daftar soal)
  const toggleOverview = () => setShowOverview(!showOverview);

  // Lompat ke soal tertentu
  const jumpToQuestion = (index) => {
    setCurrentIndex(index);
    setShowOverview(false);
  };

  // Selesai tes
  const handleFinish = async () => {
    if (Object.keys(answers).length < questions.length) {
      setModal({
        open: true,
        type: "warning",
        message: "Harap jawab semua soal terlebih dahulu sebelum selesai.",
      });
      return;
    }

    setLoadingSubmit(true);

    try {
      // Tidak perlu submit ulang karena jawaban sudah dikirim di handleNext.
      // Langsung tampilkan modal sukses dan arahkan ke hasil.
      setModal({
        open: true,
        type: "success",
        message: "Tes selesai! Jawabanmu sudah tersimpan.",
      });
    } catch (err) {
      console.error("Gagal menyelesaikan tes:", err.response?.data || err.message);
      setModal({
        open: true,
        type: "error",
        message: "Terjadi kesalahan saat menyelesaikan tes. Mohon coba lagi.",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Kondisi loading / error
  if (loading) return <div className="text-center py-10">Memuat pertanyaan...</div>;
  if (error) return <div className="text-center py-10 text-red-500">{error}</div>;
  if (!questions.length) return <div className="text-center py-10">Tidak ada pertanyaan tersedia.</div>;

  // Data soal aktif
  const currentQuestion = questions[currentIndex];
  const selectedOptionId = answers[currentQuestion.id] || null;
  const totalQuestions = questions.length;

  return (
    <div className="min-h-fit bg-[#FDF8F0] relative overflow-hidden isolate flex flex-col items-center pt-8 pb-6 sm:pb-12 px-4 sm:px-6">


      {/* Button Overview - Moved to top for consistency */}
      <div className="w-full max-w-3xl flex justify-end px-2 mb-2 relative z-62">
        <button
          onClick={toggleOverview}
          className="bg-[#FFCE43] text-[--st-color-program-red] px-4 py-2 rounded-full font-poppins text-sm font-semibold hover:border hover:border-[--st-color-program-red]"
        >
          Lihat Overview Soal
        </button>
      </div>


      <QuestionCard
        questionText={currentQuestion.question_text}
        options={currentQuestion.options}
        selectedOptionId={selectedOptionId}
        onSelect={handleSelect}
        onPrevious={handlePrevious}
        onNext={handleNext}
        showPrevious={currentIndex > 0}
        currentIndex={currentIndex}
        totalQuestions={totalQuestions}
      />

      {showOverview && (
        <div className="fixed inset-0 z-[60] st-placement-modal-overlay flex items-center justify-center px-4 sm:px-6">
          <div className="relative z-[61] st-placement-modal-panel w-full max-w-md p-5 sm:p-6">
            <h3 className="st-placement-modal-title text-lg mb-4">Overview Soal</h3>
            <div className="grid grid-cols-5 gap-2">
              {questions.map((q, idx) => {
                const isAnswered = !!answers[q.id];
                return (
                  <button
                    key={q.id}
                    onClick={() => jumpToQuestion(idx)}
                    className={`w-10 h-10 rounded-full font-poppins text-white ${
                      isAnswered ? "bg-[#1A7A3A]" : "bg-gray-400"
                    }`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
            <button
              onClick={toggleOverview}
              className="st-btn-base st-btn-danger mt-4 w-full px-4 py-2 rounded-lg"
            >
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* Modal Custom */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center st-placement-modal-overlay px-4 sm:px-6">
          <div className="st-placement-modal-panel w-full max-w-md p-6 text-center">
            <h2
              className={`st-placement-modal-title text-xl mb-4 ${
                modal.type === "warning"
                  ? "text-yellow-500"
                  : modal.type === "success"
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {modal.type === "warning"
                ? "Peringatan"
                : modal.type === "success"
                ? "Berhasil"
                : "Error"}
            </h2>
            <p className="mb-6 text-gray-700">{modal.message}</p>
            <div className="flex justify-center gap-4">
              {modal.type === "success" ? (
                <button
                  onClick={() => navigate("/result")}
                  className="st-btn-base st-btn-primary px-4 py-2 rounded-lg"
                >
                  Lihat Hasil
                </button>
              ) : (
                <button
                  onClick={() => setModal({ ...modal, open: false })}
                  className="st-btn-base st-btn-blue px-4 py-2 rounded-lg"
                >
                  Oke
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <ProgressBar answeredCount={Object.keys(answers).length} total={questions.length} />
      {/* Popup Loading untuk Submit */}
      {loadingSubmit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center st-placement-modal-overlay px-4 sm:px-6">
          <div className="st-placement-modal-panel w-full max-w-md p-6 text-center">
            <h2 className="st-placement-modal-title text-xl mb-4 text-blue-600">Sedang Memproses</h2>
            <p className="mb-6 text-gray-700">Tunggu sebentar, jawaban Anda sedang disimpan dan dicek...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestPage;